/**
 * DailyImmersionsTracker — daily objective widget.
 *
 * Two render modes :
 *   - mode="self"  : the pre-recruteur (or admin) sees THEIR own progress
 *                    for today + a 30-day mini-history bar chart.
 *   - mode="team"  : admin-only — table of all active pre-recruteurs with
 *                    their X/4 + global aggregated bar chart.
 *
 * Business rule: minimum 4 immersions scheduled per recruteur per day.
 * Cancellations auto-deduct (backend already wipes immersion_invited_at
 * on cancel, so the count only reflects LIVE invitations).
 */
import { motion } from "framer-motion";
import { Award, ChevronDown, ChevronLeft, ChevronRight, Flame, Sparkles, Target, TrendingUp, Users2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { api } from "../../lib/api";


const WEEKDAY_LABELS = ["L", "M", "M", "J", "V", "S", "D"];


function statusTone(status) {
    if (status === "reached") return { bg: "bg-emerald-500", soft: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-300" };
    if (status === "in_progress") return { bg: "bg-amber-400", soft: "bg-amber-50", text: "text-amber-800", border: "border-amber-300" };
    return { bg: "bg-shariti-red", soft: "bg-red-50", text: "text-shariti-red", border: "border-red-300" };
}


function pluralize(n, singular, plural) {
    return `${n} ${n <= 1 ? singular : plural}`;
}


// ──────────────────────────────────────────────────── SELF VIEW


export function DailyImmersionsSelfTracker({ firstName }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get("/objectives/daily-immersions/me?days=30")
            .then(({ data }) => { if (alive) setData(data); })
            .catch(() => { /* silent — widget shouldn't kill the dashboard */ })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    if (loading) {
        return (
            <div className="card-soft p-5 animate-pulse" data-testid="daily-immersions-self-loading">
                <div className="h-5 w-44 bg-slate-200 rounded mb-2" />
                <div className="h-20 bg-slate-100 rounded-xl" />
            </div>
        );
    }
    if (!data) return null;

    const { today, history, stats, target } = data;
    const tone = statusTone(today.status);
    const fname = firstName || today.name?.split(" ")[0] || "";
    const incentive = today.status === "reached"
        ? (today.count > target
            ? `Objectif dépassé ! ${today.count} immersions programmées aujourd'hui — au top ${fname} !`
            : `Objectif atteint ! Bravo ${fname}, continuez sur cette lancée.`)
        : today.status === "in_progress"
            ? `Bonne dynamique — encore ${pluralize(today.remaining, "immersion", "immersions")} à programmer pour atteindre votre objectif du jour.`
            : `Objectif du jour non atteint. Il reste ${pluralize(today.remaining, "immersion", "immersions")} à programmer — redoublez d'efforts !`;

    const Icon = today.status === "reached" ? Award : today.status === "in_progress" ? Flame : Target;
    const peak = Math.max(target, ...history.map((h) => h.count));

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-soft overflow-hidden"
            data-testid="daily-immersions-self"
        >
            {/* Header : big counter + incentive */}
            <div className={`relative p-5 md:p-6 ${tone.soft} border-b ${tone.border}`}>
                <div className="flex items-start gap-4">
                    <div className={`h-12 w-12 rounded-2xl ${tone.bg} text-white inline-flex items-center justify-center shrink-0 shadow-btn`}>
                        <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1">
                            <Sparkles className="h-3 w-3" /> Objectif du jour
                        </div>
                        <div className="flex items-baseline gap-2 mb-1.5">
                            <span
                                className="text-5xl font-black tracking-tight text-shariti-black tabular-nums"
                                data-testid="daily-immersions-count"
                            >
                                {today.count}
                            </span>
                            <span className="text-2xl font-extrabold text-slate-400 tabular-nums">
                                / {today.target}
                            </span>
                            <span className={`text-xs font-extrabold ${tone.text} uppercase tracking-wider ml-1`}>
                                {today.status === "reached" ? "atteint" : `${today.percent}%`}
                            </span>
                        </div>
                        <p className="text-sm font-medium text-slate-700 leading-snug" data-testid="daily-immersions-incentive">
                            {incentive}
                        </p>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="mt-4 h-2 rounded-full bg-white border border-slate-200 overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${today.percent}%` }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        className={`h-full ${tone.bg}`}
                    />
                </div>
            </div>

            {/* History — 30-day mini bar chart */}
            <div className="p-5 md:p-6">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest text-slate-500">
                        <TrendingUp className="h-3.5 w-3.5" />
                        Rythme sur 30 jours
                    </div>
                    <div className="flex items-center gap-3 text-[10px] font-bold text-slate-500">
                        <span><strong className="text-shariti-black">{stats.average_per_day}</strong> /j moy.</span>
                        <span>·</span>
                        <span><strong className="text-emerald-600">{stats.days_target_reached}</strong> jours objectif atteint</span>
                    </div>
                </div>

                <div className="flex items-end gap-1 h-20" data-testid="daily-immersions-history">
                    {history.map((h) => {
                        const ratio = peak > 0 ? h.count / peak : 0;
                        const heightPct = Math.max(4, Math.round(ratio * 100));
                        const isToday = h.date === history[history.length - 1].date;
                        const reachedTone = h.reached
                            ? "bg-emerald-500"
                            : h.count > 0
                                ? "bg-amber-400"
                                : "bg-slate-200";
                        return (
                            <div
                                key={h.date}
                                className="flex-1 flex flex-col items-center gap-1 group relative"
                                title={`${h.date} — ${h.count} immersion${h.count > 1 ? "s" : ""}`}
                            >
                                <div className="w-full bg-slate-100 rounded-md relative" style={{ height: "80%" }}>
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: `${heightPct}%` }}
                                        transition={{ duration: 0.5 }}
                                        className={`absolute bottom-0 left-0 right-0 rounded-md ${reachedTone} ${isToday ? "ring-2 ring-shariti-black ring-offset-1" : ""}`}
                                    />
                                </div>
                                <span className="text-[8px] font-bold text-slate-400">
                                    {WEEKDAY_LABELS[h.weekday]}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Sub-stats line */}
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                    <div className="rounded-xl bg-slate-50 p-2">
                        <div className="text-lg font-extrabold text-shariti-black tabular-nums">{stats.total_immersions}</div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total 30j</div>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-2">
                        <div className="text-lg font-extrabold text-shariti-black tabular-nums">{stats.average_per_day}</div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Moyenne/j</div>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-2">
                        <div className="text-lg font-extrabold text-emerald-600 tabular-nums">
                            {stats.days_target_reached}<span className="text-slate-400 text-sm">/{stats.working_days_in_window}</span>
                        </div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Jours OK</div>
                    </div>
                </div>

                {/* Day-by-day expandable history */}
                <DailyBreakdown />
            </div>
        </motion.div>
    );
}


// ─────────────────────────── Expandable day-by-day breakdown ───────────────────────────
// Shows precisely WHICH candidates were scheduled each day, with weekly
// navigation. Reused by both self and team views (the backend auto-scopes).
function DailyBreakdown({ scopeUserId }) {
    const [open, setOpen] = useState(false);
    const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, -1 = previous, etc.
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Compute the Monday-Sunday range of the targeted week, in Paris timezone.
    const range = useMemo(() => {
        const today = new Date();
        const day = today.getDay() === 0 ? 7 : today.getDay(); // Mon=1, Sun=7
        const monday = new Date(today);
        monday.setDate(today.getDate() - (day - 1) + weekOffset * 7);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return { from: fmt(monday), to: fmt(sunday), monday, sunday };
    }, [weekOffset]);

    useEffect(() => {
        if (!open) return;
        let alive = true;
        setLoading(true);
        const params = { from_date: range.from, to_date: range.to };
        if (scopeUserId) params.user_id = scopeUserId;
        api.get("/objectives/daily-immersions/breakdown", { params })
            .then(({ data }) => { if (alive) setData(data); })
            .catch(() => {})
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [open, range.from, range.to, scopeUserId]);

    return (
        <div className="mt-4 border-t border-shariti-gray pt-3">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid="breakdown-toggle"
                className="w-full inline-flex items-center justify-between gap-2 text-xs font-extrabold uppercase tracking-widest text-slate-600 hover:text-shariti-orange transition py-1"
            >
                <span className="inline-flex items-center gap-1.5">
                    <ChevronDown className={`h-3.5 w-3.5 transition ${open ? "rotate-180" : ""}`} />
                    Détail jour par jour
                </span>
                <span className="text-[10px] font-bold text-slate-400 normal-case tracking-normal">
                    {open ? "masquer" : "déplier"}
                </span>
            </button>

            {open && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3"
                    data-testid="breakdown-content"
                >
                    {/* Week navigator */}
                    <div className="flex items-center justify-between mb-3">
                        <button
                            type="button"
                            data-testid="breakdown-prev-week"
                            onClick={() => setWeekOffset((o) => o - 1)}
                            className="h-8 w-8 rounded-full bg-white border border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange transition inline-flex items-center justify-center"
                            aria-label="Semaine précédente"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <div className="text-center">
                            <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">
                                Semaine du
                            </div>
                            <div className="text-sm font-extrabold text-shariti-black">
                                {range.monday.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                                {" "}→{" "}
                                {range.sunday.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: weekOffset === 0 ? undefined : "2-digit" })}
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="breakdown-next-week"
                            onClick={() => setWeekOffset((o) => Math.min(0, o + 1))}
                            disabled={weekOffset >= 0}
                            className="h-8 w-8 rounded-full bg-white border border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange transition inline-flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Semaine suivante"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>

                    {loading || !data ? (
                        <div className="text-xs text-slate-400 italic text-center py-6">Chargement…</div>
                    ) : (
                        <div className="space-y-1.5">
                            {data.days.map((day) => (
                                <DayBreakdownRow key={day.date} day={day} />
                            ))}
                        </div>
                    )}
                </motion.div>
            )}
        </div>
    );
}


const DAY_NAMES_FULL = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

function DayBreakdownRow({ day }) {
    const [expanded, setExpanded] = useState(false);
    const d = new Date(day.date + "T12:00:00");
    const isPast = new Date(day.date + "T00:00:00").getTime() < new Date().setHours(0, 0, 0, 0);
    const tone = day.reached
        ? "border-emerald-300 bg-emerald-50/40"
        : day.count > 0
            ? "border-amber-300 bg-amber-50/40"
            : isPast
                ? "border-red-200 bg-red-50/30"
                : "border-shariti-gray bg-white";

    return (
        <div className={`rounded-xl border ${tone} overflow-hidden`} data-testid={`day-row-${day.date}`}>
            <button
                type="button"
                onClick={() => setExpanded((x) => !x)}
                disabled={day.candidates.length === 0}
                className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-white/60 transition disabled:cursor-default"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 w-7 text-left">
                        {DAY_NAMES_FULL[day.weekday].slice(0, 3)}
                    </span>
                    <span className="text-sm font-extrabold text-shariti-black tabular-nums">
                        {d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                    </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-sm font-black tabular-nums ${day.reached ? "text-emerald-700" : day.count > 0 ? "text-amber-700" : "text-slate-400"}`}>
                        {day.count}<span className="opacity-50">/{day.target}</span>
                    </span>
                    {day.candidates.length > 0 && (
                        <ChevronDown className={`h-3.5 w-3.5 text-slate-500 transition ${expanded ? "rotate-180" : ""}`} />
                    )}
                </div>
            </button>
            {expanded && day.candidates.length > 0 && (
                <div className="bg-white/80 px-3 py-2 border-t border-shariti-gray space-y-1">
                    {day.candidates.map((c, idx) => (
                        <a
                            key={c.id}
                            href={`/candidats/${c.id}`}
                            className="flex items-center justify-between gap-2 text-xs hover:bg-shariti-orange-soft/30 rounded-lg px-1.5 py-1 transition"
                            data-testid={`day-candidate-${c.id}`}
                        >
                            <span className="flex items-center gap-2 min-w-0 flex-wrap">
                                <span className="font-bold text-slate-400 tabular-nums">{idx + 1}.</span>
                                <span className="font-extrabold text-shariti-black break-words">{c.name}</span>
                                {c.recruiter_name && (
                                    <span className="text-[10px] text-slate-500 font-semibold break-words">
                                        · par {c.recruiter_name}
                                    </span>
                                )}
                            </span>
                            <span className="flex items-center gap-1.5 shrink-0">
                                {c.scheduled_at && (
                                    <span className="text-[10px] font-bold text-slate-500 tabular-nums">
                                        {new Date(c.scheduled_at).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                    </span>
                                )}
                                {c.status && (
                                    <StatusPill status={c.status} />
                                )}
                            </span>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}


function StatusPill({ status }) {
    const map = {
        immersion_scheduled: { label: "Programmé", cls: "bg-amber-100 text-amber-800" },
        immersion_done: { label: "Effectué", cls: "bg-sky-100 text-sky-800" },
        awaiting_validation: { label: "À valider", cls: "bg-purple-100 text-purple-800" },
        validated: { label: "Validé", cls: "bg-emerald-100 text-emerald-800" },
        onboarding: { label: "Onboarding", cls: "bg-emerald-100 text-emerald-800" },
        hired: { label: "Embauché", cls: "bg-emerald-100 text-emerald-800" },
    };
    const m = map[status];
    if (!m) return null;
    return (
        <span className={`text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${m.cls}`}>
            {m.label}
        </span>
    );
}


// ──────────────────────────────────────────────────── TEAM VIEW (admin)


export function DailyImmersionsTeamTracker() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get("/objectives/daily-immersions/team?days=30")
            .then(({ data }) => { if (alive) setData(data); })
            .catch(() => {})
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    if (loading) {
        return (
            <div className="card-soft p-5 animate-pulse" data-testid="daily-immersions-team-loading">
                <div className="h-5 w-44 bg-slate-200 rounded mb-2" />
                <div className="h-32 bg-slate-100 rounded-xl" />
            </div>
        );
    }
    if (!data || data.today.active_recruiters === 0) return null;

    const { today, rows, history, target_per_recruiter } = data;
    const peak = Math.max(today.team_target, ...history.map((h) => h.count));

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-soft p-5 md:p-6"
            data-testid="daily-immersions-team"
        >
            <header className="flex items-start justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-shariti-black text-white inline-flex items-center justify-center">
                        <Users2 className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="text-lg font-extrabold tracking-tight text-shariti-black">
                            Performance équipe — aujourd&apos;hui
                        </h3>
                        <p className="text-xs font-medium text-slate-500 -mt-0.5">
                            Objectif : {target_per_recruiter} immersions par chargé(e) de recrutement
                        </p>
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className="text-3xl font-black text-shariti-black tabular-nums" data-testid="team-today-count">
                        {today.count}
                        <span className="text-lg font-extrabold text-slate-400">/{today.team_target}</span>
                    </div>
                    <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                        {today.percent}% atteint · {today.active_recruiters} pré-rec.
                    </div>
                </div>
            </header>

            {/* Per-recruiter rows */}
            <div className="space-y-2 mb-5">
                {rows.map((r) => (
                    <RecruiterRow key={r.user_id} row={r} />
                ))}
            </div>

            {/* Global 30-day sparkline */}
            <div className="border-t border-shariti-gray pt-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest text-slate-500">
                        <TrendingUp className="h-3.5 w-3.5" />
                        Volume global équipe — 30 derniers jours
                    </div>
                </div>
                <div className="flex items-end gap-1 h-16">
                    {history.map((h) => {
                        const ratio = peak > 0 ? h.count / peak : 0;
                        const heightPct = Math.max(4, Math.round(ratio * 100));
                        const teamReached = h.reached;
                        const tone = teamReached ? "bg-emerald-500" : h.count > 0 ? "bg-amber-400" : "bg-slate-200";
                        return (
                            <div
                                key={h.date}
                                className="flex-1 flex flex-col items-center gap-0.5 relative group"
                                title={`${h.date} — ${h.count} immersions équipe (cible ${h.team_target})`}
                            >
                                <div className="w-full bg-slate-100 rounded-md relative" style={{ height: "85%" }}>
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: `${heightPct}%` }}
                                        transition={{ duration: 0.4 }}
                                        className={`absolute bottom-0 left-0 right-0 rounded-md ${tone}`}
                                    />
                                </div>
                                <span className="text-[8px] font-bold text-slate-400">
                                    {WEEKDAY_LABELS[h.weekday]}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Day-by-day expandable team breakdown */}
                <DailyBreakdown />
            </div>
        </motion.div>
    );
}


function RecruiterRow({ row }) {
    const tone = statusTone(row.status);
    const initials = (row.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
    return (
        <div
            className={`flex items-center gap-3 rounded-xl border ${tone.border} bg-white p-2.5`}
            data-testid={`team-row-${row.user_id}`}
        >
            {row.avatar_url ? (
                <img src={row.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover shrink-0" />
            ) : (
                <div className="h-9 w-9 rounded-full bg-shariti-orange-soft text-shariti-orange font-extrabold text-xs flex items-center justify-center shrink-0">
                    {initials}
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-extrabold text-shariti-black break-words">{row.name}</span>
                    <span className={`text-sm font-black tabular-nums ${tone.text} shrink-0`}>
                        {row.count}<span className="opacity-50">/{row.target}</span>
                    </span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${row.percent}%` }}
                        transition={{ duration: 0.6 }}
                        className={`h-full ${tone.bg}`}
                    />
                </div>
            </div>
        </div>
    );
}
